import * as SeparatorPrimitive from '@radix-ui/react-separator';
import { cn } from '../../../lib/utils';

export type CharlieSeparatorProps = React.ComponentPropsWithoutRef<
  typeof SeparatorPrimitive.Root
>;

/**
 * CharlieSeparator — A visual divider between content sections.
 *
 * Built on top of [Radix Separator](https://www.radix-ui.com/primitives/docs/components/separator)
 * and styled to match the [ShadCN Separator](https://ui.shadcn.com/docs/components/separator).
 *
 * ## Usage
 *
 * ```tsx
 * import { CharlieSeparator } from '@ver.id/libs.ui.charlie';
 *
 * // Horizontal (default)
 * <CharlieSeparator />
 *
 * // Vertical
 * <CharlieSeparator orientation="vertical" />
 *
 * // With custom spacing
 * <CharlieSeparator className="my-6" />
 * ```
 *
 * ## Gotchas
 *
 * - **Vertical orientation**: When using `orientation="vertical"`, the parent
 *   container must have a defined height (e.g. `flex` layout with `items-stretch`)
 *   for the separator to be visible, since it renders as `h-full w-px`.
 * - **Accessibility**: Renders as a `role="separator"` element by default.
 *   Set `decorative` to skip adding the ARIA role.
 */
export function CharlieSeparator({
  className,
  orientation = 'horizontal',
  decorative = true,
  ...props
}: CharlieSeparatorProps) {
  return (
    <SeparatorPrimitive.Root
      data-slot="separator"
      decorative={decorative}
      orientation={orientation}
      className={cn(
        'bg-border shrink-0',
        orientation === 'horizontal' ? 'h-px w-full' : 'h-full w-px',
        className,
      )}
      {...props}
    />
  );
}
